import { Button } from '@/components/common/Button';
import { RiskyDisclosureParagraph } from '@/components/common/DisclosureParagraph';
import FadeInBox from '@/components/common/FadeInBox';
import { FlexRow } from '@/components/common/Flex';
import Icon from '@/components/common/icons/Icon';
import {
    InfoButtonWithModal,
    showInfoModal,
} from '@/components/common/InfoModal';
import { Paragraph } from '@/components/common/Typography';
import IdvIntro from '@/components/idv/IdvIntro';
import { useTrackPageViewed } from '@/services/analytics/useTracking';
import { useUserData } from '@/services/user';
import { transparentize } from '@/util/chakra';
import { useTheme } from '@chakra-ui/react';
import {
    PersonalizedInvestmentOption,
    SUPPORTED_VALUES_OPTIONS,
} from '@common/inputs';
import { InvestmentProfile, SupportedValue } from '@common/types';
import { toTitleCase } from '@common/util/strings';
import React, { useCallback, useMemo, useState } from 'react';
import { IdvForm } from '../IdvForm';

type T = { supportedValues?: InvestmentProfile['supported_values'] };

export const SupportedValuesView = () => {
    useTrackPageViewed('IDV > SupportedValuesView');

    return (
        <IdvForm<T>
            initValues={(userProfile) => ({
                supportedValues:
                    userProfile?.investmentProfile?.supported_values ||
                    undefined,
            })}
            toMutationInput={({ supportedValues }) => ({
                investmentProfile: { supported_values: supportedValues },
            })}
            backNextNavProps={{
                onClickEventParams: {
                    location: 'IDV > SupportedValuesView',
                },
            }}
        >
            <PersonalizedInvestmentsHeader />
            <SupportedValuesContent />
        </IdvForm>
    );
};

const SupportedValuesContent = () => {
    const { userInfo } = useUserData();
    const defaultValues = userInfo?.investmentProfile?.supported_values;
    return (
        <PersonalizedInvestmentContent<SupportedValue>
            title="Industries to support"
            accentColor="green.700"
            defaultValues={defaultValues}
            options={SUPPORTED_VALUES_OPTIONS}
            infoText={[
                'When you support an industry, we’ll slightly increase your investment in companies that match that value.',
                'ie. Tesla helps fight climate change by reducing carbon emissions from vehicles. Instead of investing 1% of your portfolio into their stock, we may increase this to 2-3% if you select "fight climate change".',
            ]}
        >
            {(values) => (
                <IdvForm.Input<T>
                    name="supportedValues"
                    label=""
                    value={values}
                    display="none"
                />
            )}
        </PersonalizedInvestmentContent>
    );
};

interface Info {
    title: string;
    paragraphs: string[];
}

const getOptionsWithInfo = <T extends string>(
    options: PersonalizedInvestmentOption<T>[],
    info: Info
): PersonalizedInvestmentOption<T | 'INFO'>[] => [
    {
        value: 'INFO' as const,
        label: info.title,
        iconName: 'InfoCircle',
        paragraphs: info.paragraphs,
    },
    ...options,
];

const PersonalizedInvestmentModals = <T extends string>({
    options,
    accentColor,
}: {
    options: PersonalizedInvestmentOption<T>[];
    accentColor: string;
}) => {
    const theme = useTheme();

    const getButtonStyles = useCallback(
        (isChecked: boolean) => ({
            w: 10,
            h: 10,
            p: 0,
            mr: 3,
            minW: 'auto',
            color: accentColor,
            border: '1px solid transparent',
            borderColor: isChecked ? accentColor : 'border',
            bg: isChecked
                ? transparentize(accentColor, 0.05)(theme)
                : undefined,
            boxShadow: isChecked ? 'sm' : undefined,
            display: 'flex',
            variant: 'ghost',
            _hover: {
                borderColor: accentColor,
                boxShadow: 'sm',
            },
            _active: {
                bg: 'transparent',
            },
        }),
        [accentColor, theme]
    );

    const getModalProps = useCallback(
        (i: number, onNext: (i: number) => void) => ({
            color: 'gray',
            title: toTitleCase(options[i].label),
            children: (
                <>
                    <FlexRow top={4}>
                        {options.map(({ iconName, label }, j) => {
                            const isChecked = j === i;
                            return (
                                <Button
                                    key={`tab-${j}`}
                                    aria-label={label}
                                    onClick={() => onNext(j)}
                                    onClickEvent="Supported Values Tab Button Clicked"
                                    onClickEventParams={{ label }}
                                    {...getButtonStyles(isChecked)}
                                >
                                    <FadeInBox
                                        initial={{ opacity: 1 }}
                                        animate={{
                                            opacity: 1,
                                            transform: isChecked
                                                ? ['scale(1.1)', `scale(1)`]
                                                : ['scale(1)'],
                                        }}
                                    >
                                        <Icon name={iconName} />
                                    </FadeInBox>
                                </Button>
                            );
                        })}
                    </FlexRow>
                    {options[i].paragraphs.map((paragraph, j) => (
                        <Paragraph key={`${i}-${j}`}>{paragraph}</Paragraph>
                    ))}
                </>
            ),
        }),
        [getButtonStyles, options]
    );

    const showModal = useCallback(
        (nextIndex = 0) => showInfoModal(getModalProps(nextIndex, showModal)),
        [getModalProps]
    );

    const modalProps = useMemo(
        () => getModalProps(0, showModal),
        [getModalProps, showModal]
    );

    return (
        <InfoButtonWithModal
            position="relative"
            top="-1px"
            buttonProps={{
                onClickEventParams: { location: 'IDV > SupportedValuesView' },
            }}
            {...modalProps}
        />
    );
};

type PersonalizedInvestmentContentType<T extends string> = {
    title: string;
    accentColor: string;
    options: PersonalizedInvestmentOption<T>[];
    infoText: string[];
    defaultValues?: (T | 'INFO')[];
    children: (values?: (T | 'INFO')[]) => React.ReactNode;
};

export const PersonalizedInvestmentContent = <T extends string = any>({
    title,
    accentColor,
    options,
    infoText,
    defaultValues,
    children,
}: PersonalizedInvestmentContentType<T>) => {
    const theme = useTheme();
    const [values, setValues] = useState<(T | 'INFO')[] | undefined>(
        defaultValues || undefined
    );

    const updateValue = (value: T | 'INFO') => {
        if (!values) setValues([value]);
        else if (values.includes(value))
            setValues(values.filter((v) => v !== value));
        else setValues([...values, value]);
    };

    const info = { title, paragraphs: infoText };
    const optionsWithInfo = getOptionsWithInfo<T | 'INFO'>(options, info);

    return (
        <>
            <FlexRow mb={4} position="relative" align="center">
                <Paragraph fontWeight="semibold">{title}</Paragraph>
                <PersonalizedInvestmentModals<T | 'INFO'>
                    options={optionsWithInfo}
                    accentColor={accentColor}
                />
            </FlexRow>
            <FlexRow
                display="grid"
                gap={3}
                gridTemplateColumns="1fr 1fr"
                mt={2}
            >
                {options.map(({ value, label, iconName }) => {
                    const isChecked = values?.includes(value);
                    return (
                        <Button
                            key={value}
                            aria-label={label}
                            onClick={(event) => {
                                event.preventDefault();
                                updateValue(value);
                            }}
                            onClickEvent="Supported Value Button Clicked"
                            onClickEventParams={{ label, checked: !isChecked }}
                            variant="unstyled"
                            alignItems="flex-start"
                            border="1px solid transparent"
                            borderColor={isChecked ? accentColor : 'border'}
                            borderRadius="6px"
                            boxShadow={isChecked ? 'sm' : undefined}
                            bg={
                                isChecked
                                    ? transparentize(accentColor, 0.05)(theme)
                                    : 'white'
                            }
                            color={accentColor}
                            display="flex"
                            flexDir="column"
                            h="auto"
                            justifyContent="flex-start"
                            p={6}
                            py={4}
                            transition="all 150ms ease-out"
                            userSelect="none"
                            _hover={{ borderColor: accentColor }}
                        >
                            <FadeInBox
                                initial={{ opacity: 1 }}
                                animate={{
                                    opacity: 1,
                                    transform: [
                                        'scale(1.1)',
                                        `scale(${isChecked ? 1 : 0.9999})`,
                                    ],
                                }}
                            >
                                <Icon name={iconName} />
                            </FadeInBox>
                            <Paragraph
                                as="span"
                                color="gray"
                                fontWeight="medium"
                                mt={1}
                                align="left"
                                whiteSpace="pre-wrap"
                            >
                                {label}
                            </Paragraph>
                        </Button>
                    );
                })}
            </FlexRow>
            {children(values)}
            <RiskyDisclosureParagraph mt={8} />
        </>
    );
};

export const PersonalizedInvestmentsHeader = () => (
    <IdvIntro
        title="Personalize your investments"
        desc={
            <>
                We make it easy to invest based on your values. These choices
                may impact your returns.
                <InfoButtonWithModal
                    title="Values-based investing"
                    buttonProps={{
                        position: 'relative',
                        top: '-1px',
                        onClickEventParams: {
                            location: 'IDV > SupportedValuesView',
                        },
                    }}
                    color="gray"
                >
                    <Paragraph>
                        76% of adults today believe in the importance of
                        investing with your values.
                    </Paragraph>
                    <Paragraph>
                        Most research has shown that there is a negligible
                        impact on returns, when individuals invest based on
                        values preferences. (Source: FT Adviser, April 2023)
                    </Paragraph>
                    <Paragraph>
                        When you use Plenty’s values-based investment
                        portfolios, we place guardrails to ensure your return
                        does not deviate by more than a few percent.
                    </Paragraph>
                </InfoButtonWithModal>
            </>
        }
    />
);
